import { useState } from "react";
import type { User } from "../api";

const COUNTRY_CODE = "+47";
export const formatPhone = (phone: string | null | undefined) =>
  phone?.startsWith(COUNTRY_CODE) ? `${COUNTRY_CODE} ${phone.slice(COUNTRY_CODE.length)}` : (phone ?? "");

/** Modal list of users; tapping one selects it. */
export function UserPickerDialog({
  users,
  value,
  onPick,
  onCancel,
  noneLabel = "Anyone — first to take it",
}: {
  users: User[];
  value: number | null;
  onPick: (id: number | null) => void;
  onCancel: () => void;
  noneLabel?: string | null;
}) {
  const row = (selected: boolean) =>
    `flex w-full items-center gap-3 rounded-[14px] px-4 py-3 text-left transition-colors ${
      selected ? "bg-ink text-white" : "bg-blush-soft active:bg-lavender"
    }`;
  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-ink/40 sm:items-center sm:px-6" onClick={onCancel}>
      <div
        className="pop-in flex max-h-[85dvh] w-full max-w-md flex-col gap-3 rounded-t-[22px] bg-white p-5 shadow-pop sm:rounded-[20px]"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="text-[19px] font-bold tracking-[-0.02em]">Choose user</h2>
        <ul className="flex flex-col gap-2 overflow-y-auto">
          {noneLabel && (
            <li>
              <button onClick={() => onPick(null)} className={row(value === null)}>
                <span className="font-semibold">{noneLabel}</span>
              </button>
            </li>
          )}
          {users.map((u) => (
            <li key={u.id}>
              <button onClick={() => onPick(u.id)} className={row(value === u.id)}>
                <div className="min-w-0 flex-1">
                  <div className="font-semibold">
                    {u.name}
                    {u.isDriver && <span className="ml-2 text-xs font-bold opacity-60">driver</span>}
                  </div>
                  {u.description && <div className="truncate text-xs font-semibold opacity-70">{u.description}</div>}
                </div>
                {u.phone && <span className="shrink-0 text-xs font-semibold opacity-70">{formatPhone(u.phone)}</span>}
              </button>
            </li>
          ))}
        </ul>
        <button onClick={onCancel} className="h-12 rounded-[14px] bg-white font-bold border border-line text-[#3a1c25]">
          Cancel
        </button>
      </div>
    </div>
  );
}

/** Field that looks like the other inputs and opens the picker when tapped. */
export function UserField({
  users,
  value,
  fallbackLabel,
  onChange,
  disabled,
  noneLabel,
  actionLabel,
}: {
  users: User[];
  value: number | null;
  fallbackLabel?: string | null;
  onChange: (id: number | null, user: User | null) => void;
  disabled?: boolean;
  noneLabel?: string;
  /** Renders an explicit button to the right of the field that opens the picker. */
  actionLabel?: string;
}) {
  const [open, setOpen] = useState(false);
  const chosen = users.find((u) => u.id === value) ?? null;
  const label = chosen?.name ?? (value !== null ? fallbackLabel : null) ?? noneLabel ?? "Anyone — first to take it";
  return (
    <>
      <div className="flex items-center gap-2">
        <button
          type="button"
          disabled={disabled}
          onClick={() => setOpen(true)}
          className="flex h-13 min-w-0 flex-1 items-center justify-between rounded-[14px] bg-white px-3.5 text-left font-semibold outline-none disabled:opacity-60 border border-line focus:border-[#e39aa8] focus:ring-[3px] focus:ring-rose/20"
        >
          <span className="min-w-0 flex-1 truncate">
            {label}
            {chosen?.description && <span className="ml-2 text-xs text-ink/50">{chosen.description}</span>}
          </span>
          <span className="ml-2 text-ink/40" aria-hidden>
            ▾
          </span>
        </button>
        {actionLabel && (
          <button
            type="button"
            disabled={disabled}
            onClick={() => setOpen(true)}
            className="h-13 shrink-0 rounded-[14px] bg-blush px-4 text-sm font-bold text-rose-ink transition-transform active:scale-95 disabled:opacity-60"
          >
            {actionLabel}
          </button>
        )}
      </div>
      {open && (
        <UserPickerDialog
          users={users}
          value={value}
          noneLabel={noneLabel ?? "Anyone — first to take it"}
          onPick={(id) => {
            setOpen(false);
            onChange(id, users.find((u) => u.id === id) ?? null);
          }}
          onCancel={() => setOpen(false)}
        />
      )}
    </>
  );
}
